import { useCallback, useMemo, useRef } from 'react';
import moment from 'moment';
import { NavigateOptions, useSearchParams } from 'react-router-dom';
import { COMPANY_ACTION_QUERY_PARAM } from 'common/constants/pageActions';
import {
  OPM_CALCULATOR_QUERY_PARAM_CAP_TABLE,
  OPM_CALCULATOR_QUERY_PARAM_EQUITY_VALUE,
  OPM_CALCULATOR_QUERY_PARAM_MATURITY,
  OPM_CALCULATOR_QUERY_PARAM_OPM_DATE,
  OPM_CALCULATOR_QUERY_PARAM_RISK_FREE_RATE,
  OPM_CALCULATOR_QUERY_PARAM_VOLATILITY,
  OPM_CALCULATOR_QUERY_PARAMS,
  TAB_QUERY_PARAM,
} from 'pages/ValuationsAllocation/common/constants/valuations';
import { ISO_DATE_FORMAT } from 'utilities';
import { OPMCalculatorUrlInputs, UseOPMCalculatorUrlState } from './useOPMCalculatorUrlState.props';

const PARAM_BY_INPUT_KEY: Array<[keyof OPMCalculatorUrlInputs, string]> = [
  ['capTableId', OPM_CALCULATOR_QUERY_PARAM_CAP_TABLE],
  ['equityValue', OPM_CALCULATOR_QUERY_PARAM_EQUITY_VALUE],
  ['riskFreeRate', OPM_CALCULATOR_QUERY_PARAM_RISK_FREE_RATE],
  ['maturity', OPM_CALCULATOR_QUERY_PARAM_MATURITY],
  ['volatility', OPM_CALCULATOR_QUERY_PARAM_VOLATILITY],
  ['opmDate', OPM_CALCULATOR_QUERY_PARAM_OPM_DATE],
];

// Only params the caller actually supplied are touched, so a partial update leaves the rest of
// the calculator's URL state alone. A supplied `undefined` clears its param.
const applyInputs = (searchParams: URLSearchParams, inputs: Partial<OPMCalculatorUrlInputs>) => {
  PARAM_BY_INPUT_KEY.forEach(([key, param]) => {
    if (!Object.prototype.hasOwnProperty.call(inputs, key)) {
      return;
    }
    const value = inputs[key];
    if (value === undefined || value === null || value === '' || Number.isNaN(value)) {
      searchParams.delete(param);
    } else {
      searchParams.set(param, String(value));
    }
  });

  return searchParams;
};

/** A finite number, or undefined when the param is absent or malformed. */
const readNumber = (raw: string | null): number | undefined => {
  if (raw === null || raw === '') {
    return undefined;
  }
  const value = Number(raw);
  return Number.isFinite(value) ? value : undefined;
};

/** A strict ISO `YYYY-MM-DD` date, or undefined. Guards against `?opm-date=12/31/2024`. */
const readDate = (raw: string | null): string | undefined =>
  (raw && moment(raw, ISO_DATE_FORMAT, true).isValid() ? raw : undefined);

/** A positive safe integer id, or undefined. Guards against `?opm-cap-table=abc`. */
const readId = (raw: string | null): number | undefined => {
  const value = readNumber(raw);
  return value !== undefined && Number.isSafeInteger(value) && value > 0 ? value : undefined;
};

/**
 * Mirrors the OPM Calculator inputs into the query string, the way the Waterfall pages carry
 * their calculation inputs, so a filled-in calculator survives a reload and can be shared.
 *
 * Uses `useSearchParams` rather than Waterfall's `useNavigate` + `query-string` to match the
 * local precedent (`useApproachQueryParam`), which already owns the `tab` param on this page.
 */
const useOPMCalculatorUrlState = (): UseOPMCalculatorUrlState => {
  const [searchParams, setSearchParams] = useSearchParams();

  /**
   * The params every update builds on. react-router 6 hands a functional `setSearchParams` the
   * `searchParams` of the render that created the setter, not the live URL, so an update fired
   * from an awaited continuation rebuilt the query string from before the edit that started it.
   * Editing Maturity does exactly that: the FRED rate lands one round trip later and its write
   * put the previous maturity back into the URL, which then won over the row the edit had
   * already patched -- the sheet showed the old value until Save cleared the params.
   *
   * Written on every render and by every update, so a second update in the same tick (before
   * the re-render) also builds on the first.
   */
  const latestSearchParamsRef = useRef(searchParams);
  latestSearchParamsRef.current = searchParams;

  const updateSearchParams = useCallback(
    (update: (currentSearchParams: URLSearchParams) => URLSearchParams, navigateOptions?: NavigateOptions) => {
      const nextSearchParams = update(new URLSearchParams(latestSearchParamsRef.current));
      latestSearchParamsRef.current = nextSearchParams;
      setSearchParams(nextSearchParams, navigateOptions);
    },
    [setSearchParams]
  );

  const inputsFromUrl = useMemo<OPMCalculatorUrlInputs>(
    () => ({
      capTableId: readId(searchParams.get(OPM_CALCULATOR_QUERY_PARAM_CAP_TABLE)),
      equityValue: readNumber(searchParams.get(OPM_CALCULATOR_QUERY_PARAM_EQUITY_VALUE)),
      riskFreeRate: readNumber(searchParams.get(OPM_CALCULATOR_QUERY_PARAM_RISK_FREE_RATE)),
      maturity: readNumber(searchParams.get(OPM_CALCULATOR_QUERY_PARAM_MATURITY)),
      volatility: readNumber(searchParams.get(OPM_CALCULATOR_QUERY_PARAM_VOLATILITY)),
      opmDate: readDate(searchParams.get(OPM_CALCULATOR_QUERY_PARAM_OPM_DATE)),
    }),
    [searchParams]
  );

  /**
   * `replace: true` so editing an input does not push a history entry per keystroke -- the same
   * reason the Waterfall hook replaces rather than pushes.
   */
  const setInputs = useCallback(
    (inputs: Partial<OPMCalculatorUrlInputs>) => {
      updateSearchParams(currentSearchParams => applyInputs(currentSearchParams, inputs), { replace: true });
    },
    [updateSearchParams]
  );

  /**
   * The "Show OPM Calculations" links jump to a calculator panel and prefill it at once. Both
   * halves must land in a *single* `setSearchParams` call: routing the tab through
   * `useApproachQueryParam` and the inputs through `setInputs` means two updates in one tick,
   * one of which replaces while the other pushes.
   *
   * Pushes rather than replaces, so Back returns the analyst to the surface they came from.
   */
  const openCalculatorTab = useCallback(
    (tabId: string, inputs: Partial<OPMCalculatorUrlInputs>) => {
      updateSearchParams(currentSearchParams => {
        // These params are not namespaced per panel, so one left over from another calculator
        // must not seed this one.
        OPM_CALCULATOR_QUERY_PARAMS.forEach(param => currentSearchParams.delete(param));
        const nextSearchParams = applyInputs(currentSearchParams, inputs);
        nextSearchParams.set(TAB_QUERY_PARAM, tabId);
        // Mirrors `useApproachQueryParam`: a tab change may run after a transient page action
        // has already been consumed.
        nextSearchParams.delete(COMPANY_ACTION_QUERY_PARAM);
        return nextSearchParams;
      });
    },
    [updateSearchParams]
  );

  const hasInputsInUrl = useMemo(
    () => OPM_CALCULATOR_QUERY_PARAMS.some(param => searchParams.get(param) !== null),
    [searchParams]
  );

  return { inputsFromUrl, hasInputsInUrl, setInputs, openCalculatorTab };
};

export default useOPMCalculatorUrlState;
